import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import { useSnackbar } from "notistack";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import { useEmailCampaigns, useRunEmailCampaign, useDeleteEmailCampaign, useCurrentUserId } from "../../hooks/useEmail";
import EmailUserSwitcher from "./EmailUserSwitcher";

// Mirrors WhatsAppCampaignsPage.jsx's structure closely — stat cards, filterable table,
// expandable run history. Two structural differences, both because email has a materially
// different sending mechanism: (1) no template/number columns — a campaign stores its own
// subject directly, and every send goes out via domain-wide delegation rather than a
// specific registered number; (2) runs are ASYNC (BullMQ), so "Sending…" is a real status
// this page has to show and poll through, not just "Never run"/"Completed"/"Failed" the
// way WhatsApp's inline sends only ever land on.

function formatDateTime(d) {
  if (!d) return "Never";
  return new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
}

function StatCard({ icon: Icon, label, value, sub, bg, color }) {
  return (
    <Box
      sx={{
        flex: "1 1 150px",
        minWidth: 148,
        borderRadius: "14px",
        bgcolor: "#fff",
        border: "1px solid rgba(0,0,0,0.06)",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 28, height: 28, borderRadius: "9px", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon sx={{ fontSize: 15, color }} />
        </Box>
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 23, fontWeight: 800, fontFamily: "Quicksand", lineHeight: 1.1 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>{sub}</Typography>}
    </Box>
  );
}

function audienceLabel(campaign) {
  if (campaign.audienceType === "manual") {
    const count = campaign.manualTargets?.length || 0;
    return { label: `${count} selected`, sub: "Manual list" };
  }
  return campaign.planId?.name
    ? { label: campaign.planId.name, sub: "Live plan audience" }
    : { label: "Plan deleted", sub: "Audience no longer available" };
}

// A campaign has no single stored "status" field — reads its real state off its latest
// run, same "explain it the way a person would" reasoning as WhatsApp's campaignStatus.
function campaignStatus(campaign) {
  const last = campaign.runs?.[campaign.runs.length - 1];
  if (!last) return { label: "Never run", bg: "#F1F1F1", color: "#666" };
  if (last.status === "queued" || last.status === "running") return { label: "Sending…", bg: "rgba(37,99,235,0.12)", color: "#2563EB" };
  if (last.status === "failed" || (last.failedCount && !last.sentCount)) return { label: "Failed", bg: "rgba(227,76,20,0.10)", color: "#E34C14" };
  if (last.failedCount) return { label: "Partial", bg: "rgba(250,153,5,0.14)", color: "#B36B00" };
  return { label: "Completed", bg: "rgba(9,172,49,0.12)", color: "#09AC31" };
}

function failureSummary(run) {
  if (!run?.failed?.length) return "";
  const counts = new Map();
  run.failed.forEach((f) => {
    const reason = f.reason || "Unknown reason";
    counts.set(reason, (counts.get(reason) || 0) + 1);
  });
  return [...counts.entries()].map(([reason, count]) => (count > 1 ? `${count}x ${reason}` : reason)).join("\n");
}

function DeliveryMiniStats({ run }) {
  if (!run) return <Typography sx={{ fontSize: 12, color: "#B0B7BF" }}>—</Typography>;
  if (run.status === "queued" || run.status === "running") {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <CircularProgress size={12} />
        <Typography sx={{ fontSize: 12, color: "#2563EB" }}>Sending…</Typography>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={1.4} alignItems="center">
      <Tooltip title="Sent">
        <Stack direction="row" spacing={0.3} alignItems="center">
          <DoneRoundedIcon sx={{ fontSize: 14, color: "#6B7280" }} />
          <Typography sx={{ fontSize: 12 }}>{run.sentCount || 0}</Typography>
        </Stack>
      </Tooltip>
      {run.failedCount > 0 && (
        <Tooltip title="Failed">
          <Stack direction="row" spacing={0.3} alignItems="center">
            <ErrorOutlineRoundedIcon sx={{ fontSize: 14, color: "#E34C14" }} />
            <Typography sx={{ fontSize: 12, color: "#E34C14", fontWeight: 700 }}>{run.failedCount}</Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
}

function RunDetailTable({ run }) {
  const rows = [
    ...(run.sent || []).map((s) => ({ ...s, outcome: "sent" })),
    ...(run.failed || []).map((f) => ({ ...f, outcome: "failed" })),
  ];
  if (!rows.length) {
    return <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", py: 1 }}>No recipients recorded for this run.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Influencer</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Email</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Status</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF" }}>Replied</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={r.influencerId || i}>
            <TableCell sx={{ fontSize: 12.5, fontFamily: "Quicksand" }}>{r.name || "—"}</TableCell>
            <TableCell sx={{ fontSize: 12.5, color: "#6B7280" }}>{r.email || "—"}</TableCell>
            <TableCell>
              {r.outcome === "failed" ? (
                <Tooltip title={r.reason || "No reason recorded for this failure"}>
                  <Stack direction="row" spacing={0.4} alignItems="center" sx={{ display: "inline-flex" }}>
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: "#E34C14" }} />
                    <Typography sx={{ fontSize: 12.5, color: "#E34C14" }}>Failed</Typography>
                  </Stack>
                </Tooltip>
              ) : (
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <DoneRoundedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                  <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>Sent</Typography>
                </Stack>
              )}
            </TableCell>
            <TableCell>
              {r.outcome === "failed" ? (
                <Typography sx={{ fontSize: 12.5, color: "#B0B7BF" }}>—</Typography>
              ) : r.replied ? (
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <ReplyRoundedIcon sx={{ fontSize: 14, color: "#09AC31" }} />
                  <Typography sx={{ fontSize: 12.5, color: "#09AC31", fontWeight: 700 }}>Yes</Typography>
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: "#9CA3AF" }}>No</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CampaignTableRow({ campaign, onRun, onDelete, running }) {
  const [open, setOpen] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const lastRun = campaign.runs?.[campaign.runs.length - 1];
  const audience = audienceLabel(campaign);
  const status = campaignStatus(campaign);
  const failureText = failureSummary(lastRun);
  const isSending = lastRun && ["queued", "running"].includes(lastRun.status);
  const repliedCount = (lastRun?.sent || []).filter((s) => s.replied).length;

  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? "none" : undefined } }}>
        <TableCell sx={{ width: 36, pr: 0 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 700, fontFamily: "Quicksand", fontSize: 13.5 }}>{campaign.name}</Typography>
          <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>{campaign.subject}</Typography>
          {!campaign.isMine && campaign.createdBy?.fullName && (
            <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>Created by {campaign.createdBy.fullName}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <GroupsOutlinedIcon sx={{ fontSize: 15, color: "#9CA3AF" }} />
            <Box>
              <Typography sx={{ fontSize: 12.5 }}>{audience.label}</Typography>
              <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>{audience.sub}</Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12.5 }}>{formatDateTime(campaign.lastRunAt)}</Typography>
          {lastRun?.runBy?.fullName && (
            <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>by {lastRun.runBy.fullName}</Typography>
          )}
          {campaign.runs?.length > 1 && (
            <Typography sx={{ fontSize: 10.5, color: "#B0B7BF" }}>{campaign.runs.length} runs total</Typography>
          )}
        </TableCell>
        <TableCell>
          <DeliveryMiniStats run={lastRun} />
        </TableCell>
        <TableCell>
          {repliedCount > 0 ? (
            <Stack direction="row" spacing={0.4} alignItems="center">
              <ReplyRoundedIcon sx={{ fontSize: 14, color: "#09AC31" }} />
              <Typography sx={{ fontSize: 12.5, color: "#09AC31", fontWeight: 700 }}>{repliedCount}</Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: "#B0B7BF" }}>—</Typography>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{failureText}</Box>} disableHoverListener={!failureText}>
            <Chip size="small" label={status.label} sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 10.5 }} />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title={!campaign.isMine ? "Only this campaign's creator can run it" : isSending ? "Already sending" : ""}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={running ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onRun(campaign._id)}
                  disabled={running || isSending || !campaign.isMine}
                  sx={{ textTransform: "none", boxShadow: "none", fontSize: 12.5, px: 1.4, bgcolor: "#4285F4", "&:hover": { bgcolor: "#3367D6" } }}
                >
                  Run
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={campaign.isMine ? "" : "Only this campaign's creator can delete it"}>
              <span>
                <IconButton size="small" onClick={() => onDelete(campaign._id, campaign.name)} disabled={!campaign.isMine}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2, bgcolor: "#FAFBFC", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              {(!campaign.runs || campaign.runs.length === 0) ? (
                <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>Never run yet.</Typography>
              ) : (
                <Stack spacing={1}>
                  {[...campaign.runs].reverse().map((run) => {
                    const isRunOpen = expandedRunId === run._id;
                    return (
                      <Box key={run._id} sx={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", bgcolor: "#fff" }}>
                        <Box
                          onClick={() => setExpandedRunId(isRunOpen ? null : run._id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1.5,
                            py: 1,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#F3F4F6" },
                          }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {isRunOpen ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />}
                            <Typography sx={{ fontSize: 13 }}>{formatDateTime(run.runAt)}</Typography>
                            {run.runBy?.fullName && (
                              <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>· by {run.runBy.fullName}</Typography>
                            )}
                          </Stack>
                          <DeliveryMiniStats run={run} />
                        </Box>
                        <Collapse in={isRunOpen}>
                          <Box sx={{ px: 1.5, pb: 1 }}>
                            <RunDetailTable run={run} />
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const STATUS_FILTERS = [
  { value: "all", label: "All campaigns" },
  { value: "never", label: "Never run" },
  { value: "completed", label: "Completed" },
  { value: "issues", label: "Had failures" },
];

function EmailCampaignsPage() {
  const { enqueueSnackbar } = useSnackbar();

  // Each staff member's campaigns are their own — this defaults to yourself and lets you
  // switch to another internal user's, same dropdown/roster as the Email Inbox page (both
  // are per-user since every send goes out via that staff member's own Gmail mailbox).
  const currentUserId = useCurrentUserId();
  const [selectedUserId, setSelectedUserId] = useState(null);
  useEffect(() => {
    if (!selectedUserId && currentUserId) setSelectedUserId(currentUserId);
  }, [currentUserId, selectedUserId]);

  const { data, isLoading } = useEmailCampaigns(selectedUserId);
  const runMutation = useRunEmailCampaign();
  const deleteMutation = useDeleteEmailCampaign();
  const [runningId, setRunningId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const campaigns = useMemo(() => data?.campaigns || [], [data]);

  const stats = useMemo(() => {
    let totalSent = 0, totalFailed = 0, totalReplied = 0;
    for (const c of campaigns) {
      for (const run of c.runs || []) {
        totalSent += run.sentCount || 0;
        totalFailed += run.failedCount || 0;
        totalReplied += (run.sent || []).filter((s) => s.replied).length;
      }
    }
    const repliedPct = totalSent ? Math.round((totalReplied / totalSent) * 100) : 0;
    return { totalCampaigns: campaigns.length, totalSent, totalFailed, repliedPct };
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (q && !`${c.name} ${c.subject || ""}`.toLowerCase().includes(q)) return false;
      if (statusFilter === "never" && c.runs?.length) return false;
      if (statusFilter === "completed") {
        const last = c.runs?.[c.runs.length - 1];
        if (!last || last.status !== "completed" || last.failedCount) return false;
      }
      if (statusFilter === "issues") {
        const last = c.runs?.[c.runs.length - 1];
        if (!last || (!last.failedCount && last.status !== "failed")) return false;
      }
      return true;
    });
  }, [campaigns, search, statusFilter]);

  const handleRun = async (id) => {
    setRunningId(id);
    try {
      await runMutation.mutateAsync(id);
      enqueueSnackbar("Campaign queued — sending now", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to run campaign", { variant: "error" });
    } finally {
      setRunningId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete campaign "${name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      enqueueSnackbar("Campaign deleted", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to delete campaign", { variant: "error" });
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <DashboardHeaderSection hideSearch />
      <Container sx={{ marginTop: 8, pb: 6, pl: { xs: 2, sm: 3, lg: "108px" }, pr: { xs: 2, sm: 3 } }} maxWidth="xl">
        <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", gap: 1 }}>
              <MailOutlineIcon sx={{ color: "#4285F4" }} /> Email Campaigns
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
              Emails sent to your Plans' influencers, each from the sender's own idiotic.media address.
            </Typography>
          </Box>
          <EmailUserSwitcher selectedUserId={selectedUserId} currentUserId={currentUserId} onChange={setSelectedUserId} />
        </Box>

        {!isLoading && campaigns.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 2.5 }}>
            <StatCard icon={CampaignOutlinedIcon} label="Campaigns" value={stats.totalCampaigns} bg="rgba(37,99,235,0.12)" color="#2563EB" />
            <StatCard icon={SendRoundedIcon} label="Emails sent" value={stats.totalSent} sub={stats.totalFailed ? `${stats.totalFailed} failed` : "0 failed"} bg="rgba(9,172,49,0.12)" color="#09AC31" />
            <StatCard icon={ReplyRoundedIcon} label="Replied" value={`${stats.repliedPct}%`} bg="rgba(9,172,49,0.12)" color="#09AC31" />
          </Stack>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : campaigns.length === 0 ? (
          <Paper sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)", py: 6, textAlign: "center" }}>
            <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280" }}>
              No campaigns yet — send an email to a Plan's influencers from the Plan's Summary page to
              create one, reusable any time from here.
            </Typography>
          </Paper>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search by campaign or subject"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ bgcolor: "#fff", borderRadius: "8px", flex: 1, maxWidth: { sm: 340 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ bgcolor: "#fff", borderRadius: "8px" }}>
                  {STATUS_FILTERS.map((f) => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontSize: 13.5 }}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {filteredCampaigns.length === 0 ? (
              <Paper sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)", py: 5, textAlign: "center" }}>
                <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280" }}>No campaigns match this search/filter.</Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "none" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ "& th": { fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.3, bgcolor: "#FAFBFC", borderBottom: "1px solid rgba(0,0,0,0.06)" } }}>
                      <TableCell sx={{ width: 36 }} />
                      <TableCell>Campaign</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Last run</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Replied</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCampaigns.map((c) => (
                      <CampaignTableRow
                        key={c._id}
                        campaign={c}
                        onRun={handleRun}
                        onDelete={handleDelete}
                        running={runningId === c._id}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default EmailCampaignsPage;
